import React, { useEffect } from 'react';
import { AlertTriangle, Trash2, AlertCircle, HelpCircle } from 'lucide-react';

export type ConfirmVariant = 'danger' | 'warning' | 'info';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  brandColor?: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'info',
  brandColor
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getIcon = () => {
    const iconClass = "w-12 h-12";
    const defaultColor = brandColor || '#2563eb';
    switch (variant) {
      case 'danger':
        return <Trash2 className={iconClass} style={{ color: '#dc2626' }} />;
      case 'warning':
        return <AlertTriangle className={iconClass} style={{ color: '#ea580c' }} />;
      case 'info':
        return <HelpCircle className={iconClass} style={{ color: defaultColor }} />;
    }
  };

  const getConfirmButtonStyle = () => {
    const baseColor = variant === 'danger' ? '#dc2626' : variant === 'warning' ? '#ea580c' : (brandColor || '#2563eb');
    return {
      backgroundColor: baseColor,
      color: '#ffffff'
    };
  };

  const getCancelButtonStyle = () => {
    const baseColor = variant === 'danger' ? '#dc2626' : variant === 'warning' ? '#ea580c' : (brandColor || '#2563eb');
    return {
      borderColor: baseColor,
      color: baseColor
    };
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50" />

      <div
        className="relative bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-message"
      >
        <div className="p-6">
          <h3
            id="confirm-title"
            className="text-lg font-semibold text-gray-900 mb-2"
          >
            {title}
          </h3>

          <p
            id="confirm-message"
            className="text-gray-600 text-sm leading-relaxed mb-6 whitespace-pre-wrap"
          >
            {message}
          </p>

          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 font-medium bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {cancelLabel}
            </button>
            <button
              onClick={handleConfirm}
              style={getConfirmButtonStyle()}
              className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:opacity-90"
              autoFocus
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
